"use client";

import type { LucideIcon } from "lucide-react";
import { BarChart3, Check, Code2, Globe2, MailCheck, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import ArrowBottom from "@/components/ui/arrow-bottom";
import ArrowBottomFlipped from "@/components/ui/arrow-bottom-flipped";

interface FeatureBucketProps {
  number: number;
  title: string;
  capability: string;
  description: string;
  features: string[];
  icon: LucideIcon;
  accent: string;
  videoSrc: string;
}

function FeatureBucket({ number, title, capability, description, features, accent, videoSrc }: FeatureBucketProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const isEven = number % 2 === 0;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        // Start loading when video is 400px away from viewport
        rootMargin: "400px",
      },
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div
          className={`${
            isEven ? "lg:order-2" : "lg:order-1"
          } group relative overflow-hidden rounded-2xl border border-orange-200/40 bg-white/95 p-10 shadow-lg transition-[transform,box-shadow] duration-300 hover:shadow-xl dark:border-orange-500/30 dark:bg-gray-900/80`}
        >
          <div
            className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accent} opacity-0 transition-opacity duration-300 group-hover:opacity-70`}
            aria-hidden="true"
          />
          <div className="relative flex items-center justify-center">
            {shouldLoad ? (
              <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="h-full w-full rounded-lg object-cover"
              >
                <source src={videoSrc} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="flex h-full min-h-[220px] w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                <div className="h-12 w-12 animate-pulse rounded-full bg-orange-200 dark:bg-orange-500/20" />
              </div>
            )}
          </div>
        </div>

        <div className={`space-y-6 md:space-y-8 ${isEven ? "lg:order-1 lg:text-left" : "lg:order-2 lg:text-left"}`}>
          <span className="inline-flex items-center gap-3 text-xs font-semibold tracking-[0.35em] text-orange-600 uppercase dark:text-orange-300">
            Step {String(number).padStart(2, "0")}
          </span>
          <div className="space-y-3">
            <h3 className="text-2xl font-semibold text-gray-900 md:text-3xl dark:text-white">{title}</h3>
            <p className="text-lg font-medium text-gray-800 md:text-xl dark:text-gray-200">{capability}</p>
            <p className="text-base leading-relaxed text-gray-600 md:text-lg dark:text-gray-300">{description}</p>
          </div>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3 text-left">
                <Check className="mt-1 h-5 w-5 shrink-0 text-orange-500 dark:text-orange-300" />
                <span className="text-base text-gray-600 md:text-lg dark:text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ArrowDivider({ flipped = false }: { flipped?: boolean }) {
  const arrowClass = "h-28 w-12 text-gray-400 dark:text-orange-900";
  return (
    <div className="mt-12 flex justify-center md:mt-16">
      {flipped ? <ArrowBottomFlipped className={arrowClass} /> : <ArrowBottom className={arrowClass} />}
    </div>
  );
}

export default function FeatureSection() {
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const features = [
    {
      number: 1,
      title: "Connect your domain",
      capability: "OG Pilot maps your brand in seconds",
      description:
        "Drop in your homepage and we pull your colors and metadata so every preview starts with your brand system.",
      features: [
        "Supports any static site, headless CMS, or framework",
        "Use a wildcard domain to cover multiple subdomains",
        "Securely verify your domain with DNS TXT records",
      ],
      icon: Globe2,
      accent:
        "from-orange-200/60 via-orange-100/30 to-transparent dark:from-orange-500/20 dark:via-orange-500/10 dark:to-transparent",
      videoSrc: "/feature-buckets/connect-domain.mp4",
    },
    {
      number: 2,
      title: "Sign up with email or Google",
      capability: "Create your workspace without friction",
      description: "Use work email or Google SSO to create your workspace",
      features: [
        "SSO-ready onboarding completes in seconds",
        "Secure authentication ensures your data is protected",
        "Easy to use and navigate",
      ],
      icon: MailCheck,
      accent:
        "from-purple-200/60 via-orange-100/20 to-transparent dark:from-purple-500/20 dark:via-orange-500/10 dark:to-transparent",
      videoSrc: "/feature-buckets/sign-up.mp4",
    },
    {
      number: 3,
      title: "Brand it and ship it",
      capability: "Customize your brand, then go live with one click",
      description:
        "Fine-tune colors and logo until every preview feels on-brand, then generate your API key right from the same screen.",
      features: [
        "We auto-extract your brand colors and logo, but you can override anything that needs adjusting",
        "Preview your social cards in real time before going live",
        "Generate an API key instantly once you're happy with the result",
        "Copy our /docs as markdown so LLMs can integrate OG Pilot into your codebase in a matter of minutes",
      ],
      icon: Code2,
      accent:
        "from-amber-200/60 via-orange-100/20 to-transparent dark:from-amber-500/20 dark:via-orange-500/10 dark:to-transparent",
      videoSrc: "/feature-buckets/branding.mp4",
    },
    {
      number: 4,
      title: "Go live with analytics",
      capability: "See exactly where your clicks come from",
      description:
        "Track platform-level performance, share wins with leadership, and keep iterating until every share converts.",
      features: [
        "Daily shares and click-throughs graphed over time",
        "Shares breakdown by channel, device, and referral source",
        "Leaderboard: view top-performing links and pages",
        "Pie charts for share channel and device segmentation",
        "Exportable data for detailed post-mortems",
      ],
      icon: BarChart3,
      accent:
        "from-rose-200/60 via-orange-100/20 to-transparent dark:from-rose-500/20 dark:via-orange-500/10 dark:to-transparent",
      videoSrc: "/feature-buckets/analytics.mp4",
    },
  ] satisfies FeatureBucketProps[];

  return (
    <section id="feature-buckets" className="bg-white dark:bg-gray-950">
      <div className="container mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <div ref={sectionRef} className="space-y-12 md:space-y-16">
          <div className="grid grid-cols-1 items-center gap-10 md:gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
            <div className="space-y-6 text-left">
              <p className="text-xs font-semibold tracking-[0.35em] text-orange-600 uppercase dark:text-orange-300">
                THE SOLUTION
              </p>
              <h2 className="text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl dark:text-white">
                Dynamically generate branded social previews and get up to 3x more clicks
              </h2>
              <p className="max-w-2xl text-lg leading-relaxed text-gray-600 md:text-xl dark:text-gray-300">
                OG Pilot turns your website into revenue-ready social previews. No design tools required.
              </p>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative rounded-full bg-gradient-to-br from-orange-200 via-orange-100 to-white p-8 shadow-xl dark:from-orange-500/30 dark:via-orange-500/20 dark:to-transparent">
                <Sparkles className="h-20 w-20 text-orange-600 sm:h-24 sm:w-24 dark:text-orange-200" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {features.map((feature, index) => {
        const gradientLayers = [
          "bg-gradient-to-b from-white via-orange-50/60 to-orange-100/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900/80",
          "bg-gradient-to-b from-orange-100/30 via-white to-white dark:from-gray-900/80 dark:via-gray-900 dark:to-gray-950",
        ];

        return (
          <div
            key={feature.number}
            className={`py-16 sm:py-20 lg:py-24 ${gradientLayers[index % gradientLayers.length]}`}
          >
            <FeatureBucket {...feature} />
            <ArrowDivider flipped={index % 2 !== 0} />
          </div>
        );
      })}
    </section>
  );
}
